import type {
  SuggestionItemType,
  UrlSuggestionItemType,
} from '@moonpig/web-core-nav'
import { Reminder } from '@moonpig/web-core-nav/src/Search/Reminders/Reminder'
import {
  MAX_ALL_SUGGESTIONS,
  MAX_SUGGESTIONS,
} from '@moonpig/web-core-nav/src/Search/constants'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import {
  SearchFallbackItemType,
  SubmitItemType,
} from '@moonpig/web-core-nav/src/Search'
import type {
  InspirationItemType,
  ReminderItemType,
} from '@moonpig/web-core-nav/src/Search/types'
import {
  trackReminderClickEvent,
  trackSuggestionClickEvent,
  departmentToCategory,
} from './tracking'
import { SearchSuggestion } from './suggestions/Suggestions'
import { FlatNavigationalSuggestion } from './navigationalSuggestions'
import { createSearchGAEvent } from './analytics'
import { SearchInspirationType as Inspiration } from './searchInspirations/useSearchInspirations'

const calculateSuggestionMetrics = ({
  suggestion,
  items,
}: {
  suggestion: SuggestionItemType
  items: SearchSuggestion[]
}): {
  position: number
  count: number
} => {
  const isAllDepartments =
    suggestion.department?.activeDepartment?.name === 'all'

  const position = isAllDepartments
    ? items.findIndex(x => x.searchKey === suggestion.searchKey) + 1
    : items
        .filter(
          item =>
            item.department.enum ===
            suggestion?.department?.activeDepartment?.enum,
        )
        .findIndex(x => x.searchKey === suggestion.searchKey) + 1

  const count = isAllDepartments
    ? Math.min(items.length, MAX_ALL_SUGGESTIONS)
    : /* istanbul ignore next */ suggestion.department?.numberOfItems ||
      MAX_SUGGESTIONS

  return { position, count }
}

export const handleSubmitReminderSuggestion = ({
  reminders,
  submitItem,
  department,
}: {
  reminders: Reminder[] | undefined
  submitItem: ReminderItemType
  department: string
}) => {
  if (reminders) {
    const reminder = submitItem as ReminderItemType
    const reminderIndex = reminders.findIndex(x => x.id === reminder.id) + 1

    trackReminderClickEvent({
      numberOfReminders: reminders.length,
      index: reminderIndex,
      department,
      occasion: submitItem.occasion,
    })
  }
}

export const handleSubmitInspirationSuggestion = ({
  inspirations,
  submitItem,
  department,
}: {
  inspirations?: Inspiration[]
  submitItem: InspirationItemType
  department: string
}) => {
  if (inspirations) {
    const inspiration = submitItem as InspirationItemType
    const inspirationIndex =
      inspirations.findIndex(x => x.value === inspiration.value) + 1

    trackGAEvent({
      event: 'search_suggestion_click',

      event_data: {
        category: 'search',
        action: 'suggestion clicked',
        label: `${submitItem.value} | ${inspirationIndex}/${
          inspirations.length
        } | ${departmentToCategory(department)}`.toLowerCase(),
        non_interaction: true,
        value: undefined,
      },

      results_data: {
        index: inspirationIndex,
        number_of_results: inspirations.length || /* istanbul ignore next */ 0,
        product_category: departmentToCategory(department),
        results_list: submitItem.value.toLowerCase(),
        input_type: 'trending',
      },
    })
  }
}

export const handleSubmitSuggestion = ({
  items,
  submitItem,
  department,
}: {
  items: SearchSuggestion[]
  submitItem: SuggestionItemType
  department: string
}) => {
  const suggestion = submitItem
  const { position, count } = calculateSuggestionMetrics({
    suggestion,
    items,
  })

  const suggestionType = suggestion.searchKey
    ? 'facet suggestion'
    : 'query suggestion'

  trackSuggestionClickEvent({
    suggestion: suggestion.value,
    position,
    count,
    department:
      suggestion.department?.name || /* istanbul ignore next */ department,
    type: 'search',
    activeTab: department,
    suggestionType,
  })
}

export const handleSubmitNavigationalSuggestion = ({
  navigationalSuggestions,
  submitItem,
  department,
}: {
  navigationalSuggestions?: FlatNavigationalSuggestion[]
  submitItem: UrlSuggestionItemType
  department: string
}) => {
  if (navigationalSuggestions) {
    const suggestion = submitItem
    trackSuggestionClickEvent({
      suggestion: suggestion.value,
      position:
        navigationalSuggestions.findIndex(x => x.url === suggestion.url) + 1,
      count: navigationalSuggestions.length,
      type: 'navigational',
      department,
      suggestionType: 'navigational suggestion',
    })
  }
}

export const handleSubmitSearchFallback = ({
  submitItem,
  selectedTab,
}: {
  submitItem: SearchFallbackItemType
  selectedTab: string
}) => {
  trackGAEvent(
    createSearchGAEvent({
      eventAction: 'keyword',
      eventLabel: `${submitItem.value} | ${selectedTab} | fallback`,
    }),
  )
}

export const handleSubmitSearch = (submitItem: SubmitItemType) => {
  const eventAction =
    submitItem.itemType === 'search'
      ? 'keyword'
      : `${submitItem.itemType} keyword`

  trackGAEvent(
    createSearchGAEvent({
      eventAction,
      eventLabel: submitItem.value,
    }),
  )
}
